/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2018, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../../filters/VisFilterProxy',
	'../../../visualizations/renderer/filter/FilterLabelHelper'
],
function(_, FilterProxy, FilterLabelHelper) {
	'use strict';

	return class LocalFiltersAPI {

		/**
		 * Enumeration of filter condition operators
		 * @public
		 * @enum
		 */
		static get CONDITIONS_OPERATORS() {
			return {
				EQUALS: 'in',
				CONTAINS: 'containsignorecase',
				STARTS_WITH: 'beginswith',
				ENDS_WITH: 'endswith'
			};
		}

		constructor (options = {}) {
			this.visualization = options.visualization;
			this.dataModel = options.dataModel;
			this.filterSupport = options.filterSupport;
			this._localFilters = this.filterSupport.getLocalFilters();
			this._proxy = new FilterProxy({
				input: this._localFilters,
				output: this._localFilters
			});
		}

		destroy() {
			this.visualization = null;
			this.filterSupport = null;
			this._localFilters = null;
			this._proxy = null;
		}

		/**
		 * Get the list of filter UI descriptions
		 * @public
		 * @function LocalFiltersAPI#getFiltersDesc
		 * @returns {Object[]} array of filter descriptions
		 */
		getFiltersDesc() {
			const filterLabelHelper = new FilterLabelHelper({ 'dataSource' : this.visualization.getDataSource(), 'dashboardApi': this.filterSupport.dashboardApi });
			const filters = this.filterSupport.getFilterInfo();
			if (filters.localFilters.length > 0) {
				return filterLabelHelper.generateFilterListItems(filters.localFilters);
			}
			return [];
		}

		/**
		 * @function LocalFiltersAPI#getFilterList
		 * @description Get the list of local filter specifications
		 * @returns {Object[]} array of local filter specifications
		 */
		getFilterList() {
			return this._localFilters && this._localFilters.models.map((filterEntry) => {
				const filterEntrySpec = filterEntry.toJSON();
				// assign computed property for filter spec
				filterEntrySpec.readOnly = !this.filterSupport.isFilterEditable(filterEntry);
				return filterEntrySpec;
			});
		}

		/**
		 * Check whether a local filter for a data item exists or not
		 * @public
		 * @function LocalFiltersAPI#filterExists
		 * @param {Object} itemContext Context that describes a data item
		 * @param {string} itemContext.itemId data item identifier
		 * @param {string[]} [itemContext.itemIds] data item identifiers for data point filters
		 * @param {string} [item.aggregationType] data item aggregation type
		 * @returns {boolean} true if a local filter for the data item exists, otherwise false
		 */
		filterExists(itemContext) {
			const key = this._getKey(itemContext);
			return !!this._localFilters.getFilterEntry(key);
		}

		_setOptionToLegacyOptions(setOptions = {}){
			const payloadData = setOptions.payloadData || {};
			payloadData.undoRedoTransactionId = payloadData.undoRedoTransactionId || setOptions.transactionId || _.uniqueId('_updateFilter');
			return { payloadData: payloadData };
		}

		/**
		 * Add a new local filter to the widget
		 * @public
		 * @async
		 * @function LocalFiltersAPI#addFilter
		 * @param {Object} itemContext Context that describes a data item
		 * @param {string} itemContext.itemId data item identifier
		 * @param {string[]} [itemContext.itemIds] data item identifiers for data point filters
		 * @param {string} [itemContext.aggregationType] data item aggregation type
		 * @param {string} [itemContext.filterType] data item of type 'filter'
		 * @param {Object} [filterContext] Filter context object
		 * @param {Object[]} [filterContext.values] Array of values applied to the filter
		 * @param {Object} [filterContext.from] Start range value
		 * @param {Object} [filterContext.to] End range value
		 * @param {boolean} [filterContext.exclude] Use filter values as exclusions
		 * @param {Object} [filterContext.conditionsInfo] filter conditions information
		 * @param {Object} [filterContext.conditionsInfo.conditions] filter conditions
		 * @param {Object} [filterContext.conditionsInfo.conditions.operator = 'containsignorecase'] filter condition operator
		 * @param {Object} [filterContext.conditionsInfo.conditions.values] filter condition values
		 * @param {string[]} [filterContext.conditionsInfo.excludedValues = []]
		 * @example
		 *	filters.addFilter({
		 *		itemId: 'sales_and_marketing_csv.Product_line'
		 *	}, {
		 *		values: [{
		 *			u: 'sales_and_marketing_csv.Year_->[2006]',
		 *			d: '2006'
		 *		}]
		 *	});
		 *
		 *	filters.addFilter({
		 *		itemId: 'sales_and_marketing_csv.Quantity'
		 *	}, {
		 *		from: {
		 *			u: 0,
		 *			d: 0
		 *		},
		 *		to: {
		 *			u: 500000,
		 *			d: 500000
	 	 *		}
		 *	});
		 */
		addFilter(itemContext, filterContext, setOptions) {
			_.defaults(filterContext, {
				action: 'keepexclude',
				exclude: false
			});

			this._updateFilter(itemContext, filterContext, setOptions);
		}

		/**
		 * Edit an existing filter
		 * @public
		 * @async
		 * @function LocalFiltersAPI#editFilter
		 * @param {Object} itemContext Context that describes a data item
		 * @param {string} itemContext.itemId data item identifier
		 * @param {string[]} [itemContext.itemIds] data item identifiers for data point filters
		 * @param {string} [itemContext.aggregationType] data item aggregation type
		 * @param {Object} [filterContext] Filter context object
		 * @param {Object[]} [filterContext.values] Array of values applied to the filter
		 * @param {Object} [filterContext.from] Start range value
		 * @param {Object} [filterContext.to] End range value
		 * @param {boolean} [filterContext.exclude] Use filter values as exclusions
		 * @example
		 *	filters.editFilter({
		 *		itemId: 'sales_and_marketing_csv.Product_line'
		 *	}, {
		 *		values: [{
		 *			u: 'sales_and_marketing_csv.Year_->[2006]',
		 *			d: '2006'
		 *		}]
		 *	});
		 */
		editFilter(itemContext, filterContext, setOptions) {
			// ensure the filter already exists
			if (!this.filterExists(itemContext)) {
				return;
			}

			this._updateFilter(itemContext, filterContext, setOptions);
		}

		/**
		 * Remove an existing filter
		 * @public
		 * @async
		 * @function LocalFiltersAPI#removeFilter
		 * @param {Object} itemContext Context that describes a data item
		 * @param {string} itemContext.itemId data item identifier
		 * @param {string[]} [itemContext.itemIds] data item identifiers for data point filters
		 * @param {string} [item.aggregationType] data item aggregation type
		 * @example
		 *	filters.removeFilter({
		 *		itemId: 'sales_and_marketing_csv.Product_line'
		 *	});
		 */
		removeFilter(itemContext, setOptions) {
			const options = this._setOptionToLegacyOptions(setOptions);

			if (this.filterExists(itemContext)) {
				let key = this._getKey(itemContext);
				if (this._localFilters.removeFilterEntry(key)) {
					this._localFilters.allFilterModificationComplete(options);
					return true;
				}
			}
			return false;
		}

		_getKey (itemContext) {
			if (itemContext.itemIds) {
				return {
					id: this._localFilters.getDataPointFilterKey(itemContext.itemIds)
				};
			}
			const filterType = itemContext.aggregationType ? this._localFilters.FILTERTYPE_ORDINAL : this._localFilters.FILTERTYPE_CATEGORY;
			return this._localFilters.getKey(itemContext.itemId, filterType, itemContext.aggregationType);
		}

		_updateFilter(itemContext, filterContext, setOptions) {

			const options = this._setOptionToLegacyOptions(setOptions);

			if (filterContext.from || filterContext.to) {
				this._proxy.updateRangeFilter(itemContext, filterContext, options);
				return;
			}

			if (!itemContext.itemId && _.isArray(itemContext.itemIds)) {
				this._proxy.addDataPoints(itemContext, filterContext, options);
			} else {
				//supply defaults to the conditions in case the itemId, operator, or valueType is omitted. This handles the case where
				//there are multiple conditions (and, or)
				if (filterContext.conditionsInfo && filterContext.conditionsInfo.conditions) {
					this._updateFilterDefaults(itemContext, filterContext.conditionsInfo.conditions);
				}
				this._proxy.updateFilter(itemContext, filterContext, options);
			}
		}

		_updateFilterDefaults(itemContext, conditions) {
			const compound = conditions.and || conditions.or;
			if (compound) {
				_.each(compound, (condition) => {
					this._updateFilterDefaults(itemContext, condition);
				});
			} else {
				if (conditions.not) {
					this._defaultConditions(itemContext, conditions.not);
				} else {
					this._defaultConditions(itemContext, conditions);
				}
			}
		}

		_defaultConditions(itemContext, conditions) {
			_.defaults(conditions, {
				itemId: itemContext.itemId,
				operator: LocalFiltersAPI.CONDITIONS_OPERATORS.CONTAINS,
				valueType: 'display'
			});
		}

	};
});
